import { FrequencyValue } from "basis";
import {
  ApplicationStatus,
  FrequencyTypeUpperCase,
  GetApplicationResponse,
  SubmitApplicationResponse,
} from "components/IncreaseCreditLimit/types";
import { parseGetApplicationResponse } from "components/IncreaseCreditLimit/utils";
import { FormErrorsType } from "components/types";
import { getHref } from "library/api";
import { DeepMap, FieldError } from "react-hook-form";
import {
  ExpensesFrequencyValue,
  MonthlyExpensesType,
  NzParseSubmitApplicationResponseReturnType,
  NzSubmitApplicationRequest,
  State,
  YesNoType,
} from "./types";

export async function getApplicationInfo(getApplicationUrl: string) {
  const response = await getHref<GetApplicationResponse>(getApplicationUrl);
  const responseData = parseGetApplicationResponse(response.data);
  if (
    responseData === null ||
    responseData.application.status === ApplicationStatus.INTERNAL_SERVICE_ERROR
  ) {
    // TODO: RL-487 will handle status INTERNAL_SERVICE_ERROR
    // TODO: need user story to handle responseData === null, now following what's happening in AU flow
    throw new window.Error("Invalid application response received");
  }

  return responseData;
}

export function getFrequencyValue(expense: FrequencyValue) {
  const amount = Number(expense.amount);
  switch (expense.frequency) {
    case "weekly":
      return amount * 4.333333;
    case "fortnightly":
      return (amount * 26) / 12;
    case "quarterly":
      return amount / 3;
    case "monthly":
    default:
      return amount;
  }
}

export function checkExpensesFrequencyValues(
  formValues: ExpensesFrequencyValue,
  values: string,
): boolean {
  return Object.values(formValues).every(
    (expense) => expense.amount === values,
  );
}

export function isInvalidField(
  value: string | number | null,
  maxValue?: number,
) {
  if (maxValue) {
    return Number(value) > maxValue;
  }
  return value && Number(value) === 0;
}
export const getTotalMonthlyIncome = (
  values: {
    netIncome: FrequencyValue;
    otherIncome: FrequencyValue;
    otherSourcesOfIncome: YesNoType;
  },
  errors?: FormErrorsType,
): number | null => {
  const { netIncome, otherIncome, otherSourcesOfIncome } = values;
  if (
    (errors && (errors.netIncome || errors.otherIncome)) ||
    (netIncome.amount === "" && otherIncome.amount === "")
  ) {
    return null;
  }
  return otherSourcesOfIncome === "yes"
    ? getFrequencyValue(netIncome) + getFrequencyValue(otherIncome)
    : getFrequencyValue(netIncome);
};

export const getTotalMonthlyExpenses = (
  expenses: ExpensesFrequencyValue,
  errors?: DeepMap<MonthlyExpensesType, FieldError>,
): number | null => {
  if (
    (errors && Object.keys(errors).length > 0 && !errors.nzRevolvingMortgage) ||
    checkExpensesFrequencyValues(expenses, "")
  ) {
    return null;
  }
  const amounts = Object.values(expenses).map((expense) =>
    getFrequencyValue(expense),
  );

  if (amounts.some((amount) => isNaN(amount))) {
    return null;
  }

  return amounts.reduce((acc, amount) => acc + amount, 0);
};

export function checkAllExpensesFrequencyValues(
  formValues: ExpensesFrequencyValue,
  hasValue: boolean,
): boolean {
  return hasValue
    ? Object.values(formValues).every((expense) => expense.amount !== "")
    : Object.values(formValues).every((expense) => expense.amount === "");
}

export const getNzApplicationPayload = (
  state: State,
  values: { agreeToConsent: boolean },
): NzSubmitApplicationRequest => ({
  privacy_policy_acknowledgement: values.agreeToConsent,
  application: {
    requested_credit_limit: Number(state.newCreditLimit),
    personal_information: {
      relationship_status: state.personalInformation.relationshipStatus,
      number_of_dependants: Number(
        state.personalInformation.numberOfDependants,
      ),
    },
    residential_address: {
      residential_status: state.residentialAddress.residentialStatus,
      mortgage_or_rent: Number(state.residentialAddress.mortgageOrRent),
      time_at_current_address_year: Number(
        state.residentialAddress.timeAtCurrentAddress.years,
      ),
      time_at_current_address_month: Number(
        state.residentialAddress.timeAtCurrentAddress.months,
      ),
    },
    employment_details: {
      employment_status:
        state.employmentAndIncome.employmentDetails.employmentStatus,
      occupation: state.employmentAndIncome.employmentDetails.occupation,
      time_with_current_employer_year: Number(
        state.employmentAndIncome.employmentDetails.timeWithCurrentEmployer
          .years,
      ),
      time_with_current_employer_month: Number(
        state.employmentAndIncome.employmentDetails.timeWithCurrentEmployer
          .months,
      ),
    },
    income: {
      net_income: Number(state.employmentAndIncome.netIncome.amount),
      net_income_frequency:
        state.employmentAndIncome.netIncome.frequency.toUpperCase() as FrequencyTypeUpperCase,
      other_income: Number(state.employmentAndIncome.otherIncome.amount),
      other_income_frequency:
        state.employmentAndIncome.otherIncome.frequency.toUpperCase() as FrequencyTypeUpperCase,
    },
    monthly_expenses: {
      living_expenses_shared: state.monthlyExpenses.nzAllExpensesForHousehold,
      education_childcare: Number(
        state.monthlyExpenses.expensesFrequency.nzEducationChildcare.amount,
      ),
      transport: Number(
        state.monthlyExpenses.expensesFrequency.nzTransportAuto.amount,
      ),
      insurances: Number(
        state.monthlyExpenses.expensesFrequency.nzGeneralInsurance.amount,
      ),
      utilities: Number(
        state.monthlyExpenses.expensesFrequency.nzOtherPropertyExpenses.amount,
      ),
      personal_expenses: Number(
        state.monthlyExpenses.expensesFrequency.nzPersonalFitnessMedical.amount,
      ),
      lifestyle: Number(
        state.monthlyExpenses.expensesFrequency.nzFoodGroceriesEntertainment
          .amount,
      ),
    },
    other_cards_loans: {
      loan_repayments: Number(state.additionalDebts.nzOwingLoan),
      total_credit_limit: Number(state.additionalDebts.nzTotalCreditLimit),
      total_amounts_owing: Number(state.additionalDebts.nzTotalAmountOwing),
    },
  },
});

export function nzParseSubmitApplicationResponse(
  data: SubmitApplicationResponse,
): NzParseSubmitApplicationResponseReturnType {
  const { application } = data;

  if (!application) {
    console.error("Application is missing in the submit application response.");
    return null;
  }

  return {
    application,
  };
}

export function sleep(millisecond: number) {
  return new Promise((resolve) => setTimeout(resolve, millisecond));
}
